import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import table from './breakdownTable.module.css';

interface INameCellProps {
  value: string;
  /** A glyph after the name — the activities tab's "opens elsewhere" arrow. */
  trailing?: ReactNode;
  /** The row's action, when it has one: the name becomes a button, so the
   *  keyboard and a screen reader reach what a click on the row does. */
  onActivate?: () => void;
}

/** 
 * Name column cell shared by every breakdown panel. Caps the column width and
 *  truncates with an ellipsis so a long name (e.g. a verbose activity title)
 *  can't widen the table into horizontal scroll. The full name shows in a
 *  tooltip on hover — but only when the text is actually clipped, so short
 *  names don't trigger a redundant popup.
 *
 *  The overflow ref lives on the <td> (not the inner span): the span becomes
 *  Base UI's tooltip trigger, and Base UI needs to own that element's ref to
 *  anchor the popup — so we measure the span through the stable cell instead. */
export function NameCell({ value, trailing, onActivate }: INameCellProps) {
  const tdRef = useRef<HTMLTableCellElement>(null);
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    const td = tdRef.current;
    if (!td) return;
    const measure = () => {
      const span = td.querySelector<HTMLElement>(`.${table.nameText}`);
      if (span) setTruncated(span.scrollWidth > span.clientWidth);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(td);
    return () => observer.disconnect();
  }, [value]);

  const name = (
    <Tooltip content={truncated ? value : ''}>
      <span className={table.nameText}>
        <bdi>{value}</bdi>
      </span>
    </Tooltip>
  );

  return (
    <td ref={tdRef} className={`${table.td} ${table.name}`}>
      <span className={table.nameInner}>
        {onActivate ? (
          // Stops the click here: the row's own handler would fire it a second time.
          <button
            type="button"
            className={table.nameButton}
            onClick={(e) => {
              e.stopPropagation();
              onActivate();
            }}
          >
            {name}
          </button>
        ) : (
          name
        )}
        {trailing != null && <span className={table.nameTrailing}>{trailing}</span>}
      </span>
    </td>
  );
}
